import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './EditForm.module.css';

const EditForm = ({
  context, // New prop to differentiate between keepers and players
  selectedEntry, // Generic prop for selected entry (keeper or player)
  currentLeagueId,
  fields,
  onSubmit,
  initialValues = {},
  isEditing = false,
}) => {
  const [formData, setFormData] = useState({ ...initialValues });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const initialKeys = Object.keys(initialValues || {});
    const formKeys = Object.keys(formData || {});

    const hasDifference =
      initialKeys.length !== formKeys.length ||
      initialKeys.some((key) => formData[key] !== initialValues[key]);

    if (hasDifference) {
      setFormData({ ...initialValues });
      setErrors({});
    }
  }, [initialValues]);

  useEffect(() => {
    if (!selectedEntry) {
      // Set default values based on context
      if (context === 'keepers') {
        setFormData({
          teamManager: '',
          player: '',
          yearAcquired: '',
          acquisitionCost: '',
          keeperYear: '',
          keeperCost: '',
          league: currentLeagueId,
        });
      } else if (context === 'players') {
        // Initialize formData with default values for playerFields
        const defaultFormData = {};
        fields.forEach((field) => {
          defaultFormData[field.name] = ''; // Default to an empty string
        });
        setFormData(defaultFormData);
      }
    }
  }, [selectedEntry, currentLeagueId, context, fields]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
    setErrors(prevErrors => ({
      ...prevErrors,
      [name]: null // Clear error on input
    }));
  };

  const validate = () => {
    const newErrors = {};
    fields.forEach(field => {
      if (field.required && !formData[field.name]) {
        newErrors[field.name] = `${field.label} is required`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit(formData);
    // Optionally clear form if in create mode
    if (!isEditing) {
      setFormData({});
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles.editForm}>
      <h2 className={styles.title}>{isEditing ? 'Edit Entry' : 'Add Entry'}</h2>

      {fields.map((field) => (
        <div key={field.name} className={styles.formGroup}>
          <label htmlFor={field.name}>{field.label}</label>
          {field.type === 'select' ? (
            <select
              name={field.name}
              value={formData[field.name] || ''} // Use the value from formData
              onChange={handleChange}
              required={field.required}
              className={styles.select}
            >
              <option value="">
                {isEditing
                  ? `Select ${field.label}`
                  : field.placeholder || `Enter ${field.label}`}
              </option>
                {field.options.map((option, index) => (
                  <option key={index} value={typeof option === 'string' ? option : option.value}>
                    {typeof option === 'string' ? option : option.label}
                  </option>
              ))}
            </select>
          ) : (
            <input
              type={field.type}
              name={field.name}
              value={formData[field.name] || ''}
              onChange={handleChange}
              placeholder={
                isEditing
                ? undefined
                : field.placeholder || `Select ${field.label}`
            }
              className={styles.input}
              required={field.required}
            />
          )}
          {errors[field.name] && (
            <p className={styles.error}>{errors[field.name]}</p>
          )}
        </div>
      ))}

      <button type="submit" className={styles.submitButton}>Save</button>
    </form>
  );
};

EditForm.propTypes = {
  context: PropTypes.oneOf(['keepers', 'players']).isRequired, // New prop
  selectedEntry: PropTypes.object, // Generic prop for selected entry
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      placeholder: PropTypes.string,
      required: PropTypes.bool,
      options: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.string),
        PropTypes.arrayOf(
          PropTypes.shape({
            label: PropTypes.string,
            value: PropTypes.string,
          })
        ),
      ]),
    })
  ).isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  isEditing: PropTypes.bool,
};

export default EditForm;